import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ReactComponent as Logo } from 'assets/images/logo.svg';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Spinner } from 'common/spinner';
import { VideoClip } from 'common/video-clip';
import { appActions, AppContext } from 'context';
import { classnames, isChromeVersionWithIssues } from 'helpers/utils';
import { useHandleApiError } from 'hooks/use-handle-api-error';
import { Vault } from 'models/vault';
import { Clip } from 'models/clip';
import { MetaTags } from 'models/meta-tags';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { goToPage, RouteName } from 'routes';

import { ExpandIcon } from 'assets/icons';
import { AudioClip } from 'common/audio-clip';
import { NotificationType } from 'common/enums';
import { addLinks } from 'helpers/text-helper';
import { NotificationObject } from 'models/notificationObject';
import styles from './clip-detail-guest.module.scss';

type ParamType = {
  vaultId: string;
  clipId: string;
};

const ClipDetailGuest: React.FC = () => {
  const { dispatch } = useContext(AppContext);
  const [vault, setVault] = useState<VaultType>(new Vault());
  const [clip, setClip] = useState<ClipType>(new Clip());

  const [fetching, setFetching] = useState(true);

  const { vaultId, clipId } = useParams<ParamType>();
  const setApiError = useHandleApiError(() => {
    goToPage(RouteName.NotFoundRedirect);
  }, false);

  const getClipById = (alb: VaultType, clipIdSelected: number) =>
    alb.clips!.find((cli: ClipType) => cli.id === clipIdSelected);

  const fetchVault = async () => {
    try {
      if (vaultId) {
        const vaultResponse = await VaultsController.getSharedVault(+vaultId);
        const { title, coverThumbnailUrl } = vaultResponse;
        const description = `Check out this clip by ${title}`;
        dispatch({
          type: appActions.SET_META_TAGS,
          metaTags: new MetaTags({ description, image: coverThumbnailUrl }),
        });

        const clipToShow = getClipById(vaultResponse, +clipId);

        if (!clipToShow) {
          throw new Error('Clip does not exist');
        }

        setVault(vaultResponse);
        setClip(clipToShow);
      }
    } catch (err: any) {
      setApiError(err);
    }
  };

  useEffect(() => {
    fetchVault();
  }, []);

  useEffect(() => {
    if (isChromeVersionWithIssues()) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Problems with your browser version',
          message:
            'Your browser might experience issues playing back videos. Consider updating your browser to get the best experience.',
          type: NotificationType.Error,
          limitTimeMs: 15000,
        }),
      });
    }
  }, []);

  useEffect(() => {
    if (clip.videoUrl) {
      setFetching(false);
    }
  }, [clip]);

  const showDefaultPicture = () => (
    <div className={styles.defaultPicture}>
      <Logo className={styles.logo} />
    </div>
  );

  const showHeader = () => (
    <div className={styles.headerRow}>
      <div className={styles.leftSide}>
        <div className={styles.pictureRounded}>
          {vault.coverThumbnailUrl ? (
            <img
              src={vault.coverThumbnailUrl}
              className={styles.picture}
              alt="logo"
            />
          ) : (
            showDefaultPicture()
          )}
        </div>
        <div className={styles.titles}>
          <div className="text__body__regular__overline__gray700"> VAULT </div>
          <span
            className={classnames(styles.vaultName, 'text__heading4__gray800')}
          >
            {vault.title}
          </span>
        </div>
      </div>
    </div>
  );

  const showVideoAndDescription = () => (
    <div className={styles.videoAndDescription}>
      <div className={styles.video}>
        {clip.isAudioOnly ? (
          <div className={styles.audioOnly}>
            <AudioClip
              alt=""
              videoUrl={clip.videoUrl}
              image={clip.customThumbnail}
            />
          </div>
        ) : (
          <VideoClip clipSelected={clip} />
        )}
      </div>
      <div className={styles.info}>
        <div className={styles.clipTitle}>
          <div className="text__title1__gray800">{clip.name}</div>
          <div className="text__body__regular__medium__gray700">
            {addLinks(clip.description)}
          </div>
        </div>
        <div>
          {clip.customThumbnail && !clip.isAudioOnly && (
            <div className={classnames(styles.imageContainer)}>
              <img src={clip.customThumbnail} alt="" className={styles.image} />
              <button
                type="button"
                className={styles.expandIcon}
                onClick={() =>
                  dispatch({
                    type: appActions.SET_IMAGE_TO_ENLARGE,
                    imageToEnlarge: clip.customThumbnail,
                  })
                }
              >
                <ExpandIcon />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const showContentView = () => (
    <div className={styles.clipContainer}>
      {showHeader()}
      {showVideoAndDescription()}
    </div>
  );

  return (
    <div className={globalStyles.loggedMainContainer}>
      {!fetching ? showContentView() : <Spinner />}
    </div>
  );
};

export { ClipDetailGuest };
